import { IconType } from 'react-icons';
import { TbBolt, TbBuilding, TbCirclesRelation, TbCubeSpark, TbSparkles } from 'react-icons/tb';

import { WORKSPACE_RESOURCE_ICON_REGISTRY } from '@/shared/components/workspace-resources/WorkspaceResourceAvatar/configuration';

export type BillingInterval = 'monthly' | 'annual';
export type PlanKey = 'plus' | 'pro' | 'premium' | 'enterprise';
export type PlanUsageResourceKey =
    | 'investments'
    | 'memos'
    | 'folders'
    | 'pipelines'
    | 'teams'
    | 'watchlists'
    | 'tags'
    | 'dashboards'
    | 'lists'
    | 'dataSources'
    | 'subteamsPerTeam';
export type PlanUsageLimit = number | 'unlimited';
export type PlanFeatureKey =
    | 'investments'
    | 'memos'
    | 'folders'
    | 'pipelines'
    | 'teams'
    | 'watchlists'
    | 'tags'
    | 'subteamsPerTeam'
    | 'dashboards'
    | 'lists'
    | 'data-export';
export type PlanCapabilityKey =
    | 'rbac'
    | 'tfa'
    | 'dataExport'
    | 'apiAndWebhooks'
    | 'sso'
    | 'scim'
    | 'auditLogs'
    | 'prioritySupport'
    | 'betaProgram'
    | 'migrationAssistance';
// | 'advancedExports';

export type FeatureAddOnKey = 'lifecycles' | 'relations';
export type FeatureAddOnFeatureKey = 'initiatives' | 'actions' | 'auditReady' | 'dashboards' | 'lists';
export type FeatureAddOnCapabilityKey = 'biAnalytics' | 'dataEnrichment' | 'emailAndCalendar';
export type UsageBillingEntitlementKey =
    'vantageQueries' | 'relationsListRecords' | 'agenticWorkflows' | 'complianceWorkflows';
export type BillingFeatureKey = PlanFeatureKey | FeatureAddOnFeatureKey;
export type BillingEntitlementKey =
    BillingFeatureKey | PlanCapabilityKey | FeatureAddOnCapabilityKey | UsageBillingEntitlementKey;
export type FeatureAddOnCapabilities = Partial<Record<FeatureAddOnCapabilityKey, boolean>>;

export type PlanCapabilities = Partial<Record<PlanCapabilityKey, boolean>>;

export interface PlanPricing {
    monthly: {
        unitAmount: number;
        stripePriceId?: string;
    };

    annual: {
        monthlyEquivalent: number; // display price per month
        yearlyAmount: number; // billed yearly
        stripePriceId?: string;
    };
}

export interface PlanFeature {
    key: BillingFeatureKey;
    label: string;
    included: boolean;
    highlight?: boolean;
}

export interface PlanConfig {
    key: PlanKey;
    label: string;
    description?: string;
    icon: IconType;

    pricing?: PlanPricing;

    /** purely visual — never used for math */
    displayDiscountPercent?: {
        monthly?: number;
        annual?: number;
    };

    requiresEdu?: boolean;

    supportedIntervals?: BillingInterval[];

    features?: PlanFeature[];

    usageLimits?: Partial<Record<PlanUsageResourceKey, PlanUsageLimit>>;

    capabilities?: PlanCapabilities;

    historicalDataYears?: number | 'unlimited';

    badge?: string;
}

export interface FeatureAddOnConfig {
    key: FeatureAddOnKey;
    label: string;
    description?: string;
    icon: IconType;

    pricing?: PlanPricing;

    /** purely visual — never used for math */
    displayDiscountPercent?: {
        monthly?: number;
        annual?: number;
    };

    requiresEdu?: boolean;

    supportedIntervals?: BillingInterval[];

    features?: PlanFeature[];

    capabilities?: FeatureAddOnCapabilities;

    badge?: string;
}

export const PLAN_CONFIG: Record<PlanKey, PlanConfig> = {
    plus: {
        key: 'plus',
        label: 'Plus',
        description:
            'For independent investors looking to streamline research, due diligence, and decision-making in a centralized workspace.',
        icon: TbSparkles,
        pricing: { monthly: { unitAmount: 49 }, annual: { monthlyEquivalent: 35, yearlyAmount: 35 * 12 } },
        features: [
            { key: 'investments', label: 'Unlimited Investments', included: true },
            { key: 'memos', label: 'Unlimited Memos', included: true },
            { key: 'folders', label: '5 Folders', included: true },
            { key: 'pipelines', label: '5 Pipelines', included: true },
            { key: 'teams', label: '2 Teams', included: true },
            { key: 'watchlists', label: '10 Watchlists', included: true },
            { key: 'tags', label: '10 Tags', included: true },
            { key: 'data-export', label: 'Excel & CSV Data Downloads (5 Year Historicals)', included: true },
        ],
        usageLimits: {
            investments: 'unlimited',
            memos: 'unlimited',
            folders: 5,
            pipelines: 5,
            teams: 2,
            watchlists: 10,
            tags: 10,
            subteamsPerTeam: 0,
        },
        historicalDataYears: 5,
        capabilities: {
            prioritySupport: true,
            rbac: true,
            dataExport: true,
            tfa: true,
            betaProgram: true,
            apiAndWebhooks: false,
            sso: false,
            scim: false,
            auditLogs: false,
            migrationAssistance: false,
        },
    },
    pro: {
        key: 'pro',
        label: 'Pro',
        description:
            'For growing teams that need deeper workflows, collaboration tools, and more scalable research & due diligence capabilities.',
        icon: TbBolt,
        pricing: { monthly: { unitAmount: 149 }, annual: { monthlyEquivalent: 125, yearlyAmount: 125 * 12 } },
        displayDiscountPercent: { annual: 20 },
        features: [
            { key: 'investments', label: 'Unlimited Investments', included: true },
            { key: 'memos', label: 'Unlimited Memos', included: true },
            { key: 'folders', label: '10 Folders', included: true },
            { key: 'pipelines', label: '10 Pipelines', included: true },
            { key: 'teams', label: '5 Teams', included: true },
            { key: 'watchlists', label: '25 Watchlists', included: true },
            { key: 'tags', label: '25 Tags', included: true },
            { key: 'data-export', label: 'Excel & CSV Data Downloads (10 Year Historicals)', included: true },
        ],
        usageLimits: {
            investments: 'unlimited',
            memos: 'unlimited',
            folders: 10,
            pipelines: 10,
            teams: 5,
            watchlists: 25,
            tags: 25,
            subteamsPerTeam: 0,
        },
        historicalDataYears: 10,
        capabilities: {
            prioritySupport: true,
            rbac: true,
            dataExport: true,
            tfa: true,
            betaProgram: true,
            apiAndWebhooks: false,
            sso: false,
            scim: false,
            auditLogs: false,
            migrationAssistance: false,
        },
    },
    premium: {
        key: 'premium',
        label: 'Premium',
        description:
            'For high-performing investment teams that require advanced analytics, automation, and institutional-grade infrastructure.',
        icon: TbCubeSpark,
        pricing: { monthly: { unitAmount: 499 }, annual: { monthlyEquivalent: 395, yearlyAmount: 395 * 12 } },
        displayDiscountPercent: { annual: 20 },
        features: [
            { key: 'investments', label: 'Unlimited Investments', included: true },
            { key: 'memos', label: 'Unlimited Memos', included: true },
            { key: 'folders', label: 'Unlimited Folders', included: true },
            { key: 'pipelines', label: 'Unlimited Pipelines', included: true },
            { key: 'teams', label: 'Unlimited Teams', included: true },
            { key: 'watchlists', label: 'Unlimited Watchlists', included: true },
            { key: 'tags', label: 'Unlimited Tags', included: true },
            { key: 'subteamsPerTeam', label: '10 Subteams / Team', included: true },
            { key: 'data-export', label: 'Excel & CSV Data Downloads (30+ Year Historicals)', included: true },
        ],
        usageLimits: {
            investments: 'unlimited',
            memos: 'unlimited',
            folders: 'unlimited',
            pipelines: 'unlimited',
            teams: 'unlimited',
            watchlists: 'unlimited',
            tags: 'unlimited',
            subteamsPerTeam: 10,
        },
        historicalDataYears: 30,
        capabilities: {
            prioritySupport: true,
            rbac: true,
            dataExport: true,
            tfa: true,
            betaProgram: true,
            apiAndWebhooks: false,
            sso: false,
            scim: false,
            auditLogs: false,
            migrationAssistance: false,
        },
    },
    // education: {
    //     key: 'education',
    //     label: 'Education',
    //     description: 'Great for university students, professors, investment clubs, and more.',
    //     icon: TbSchool,
    //     pricing: { monthly: { unitAmount: 75 }, annual: { monthlyEquivalent: 65, yearlyAmount: 65 * 12 } },
    //     requiresEdu: true,
    //     displayDiscountPercent: { monthly: 25, annual: 35 },
    //     features: [
    //         { key: 'investments', label: 'Unlimited Investments', included: true },
    //         { key: 'memos', label: 'Unlimited Memos', included: true },
    //         { key: 'pipelines', label: 'Unlimited Pipelines', included: true },
    //         { key: 'teams', label: 'Unlimited Teams', included: true },
    //     ],
    //     capabilities: {
    //         prioritySupport: true,
    //         rbac: true,
    //         dataExport: true,
    //         tfa: true,
    //         betaProgram: true,
    //         apiAndWebhooks: false,
    //         sso: false,
    //         scim: false,
    //         auditLogs: true,
    //         migrationAssistance: false,
    //     },
    // },
    enterprise: {
        key: 'enterprise',
        label: 'Enterprise',
        description: `Need 10+ seats or more? We're happy to help!`,
        icon: TbBuilding,
        pricing: undefined,
        supportedIntervals: ['annual'],
        features: [
            { key: 'investments', label: 'Unlimited Investments', included: true },
            { key: 'memos', label: 'Unlimited Memos', included: true },
            { key: 'folders', label: 'Unlimited Folders', included: true },
            { key: 'pipelines', label: 'Unlimited Pipelines', included: true },
            { key: 'teams', label: 'Unlimited Teams', included: true },
            { key: 'dashboards', label: 'Unlimited Dashboards', included: true },
            { key: 'lists', label: 'Unlimited Lists', included: true },
            { key: 'watchlists', label: 'Unlimited Watchlists', included: true },
            { key: 'tags', label: 'Unlimited Tags', included: true },
            { key: 'subteamsPerTeam', label: 'Unlimited Subteams / Team', included: true },
            { key: 'data-export', label: 'Unlimited Excel & CSV Data Downloads', included: true },
        ],
        usageLimits: {
            investments: 'unlimited',
            memos: 'unlimited',
            folders: 'unlimited',
            pipelines: 'unlimited',
            teams: 'unlimited',
            dashboards: 'unlimited',
            lists: 'unlimited',
            dataSources: 'unlimited',
            watchlists: 'unlimited',
            tags: 'unlimited',
            subteamsPerTeam: 'unlimited',
        },
        historicalDataYears: 'unlimited',
        capabilities: {
            prioritySupport: true,
            rbac: true,
            dataExport: true,
            tfa: true,
            betaProgram: true,
            apiAndWebhooks: true,
            sso: true,
            scim: true,
            auditLogs: true,
            migrationAssistance: true,
        },
    },
};

export const FEATURE_ADD_ON_CONFIG: Record<FeatureAddOnKey, FeatureAddOnConfig> = {
    lifecycles: {
        key: 'lifecycles',
        label: 'Lifecycles',
        description: 'Govern recurring diligence, approvals, research lineage, evidence, and audit-ready oversight.',
        icon: WORKSPACE_RESOURCE_ICON_REGISTRY.lifecycle,
        pricing: {
            monthly: { unitAmount: 99 },
            annual: { monthlyEquivalent: 79.17, yearlyAmount: 950 },
        },
        features: [
            { key: 'initiatives', label: 'Governed Lifecycle Initiatives', included: true },
            { key: 'actions', label: 'Automated Lifecycle Actions', included: true },
            { key: 'auditReady', label: 'Audit-ready Activity & Collaboration', included: true },
        ],
    },
    relations: {
        key: 'relations',
        label: 'Relations',
        description:
            'Institutional relationship intelligence to enhance, elevate, and scale your existing investment research & due diligence.',
        icon: TbCirclesRelation,
        pricing: { monthly: { unitAmount: 39 }, annual: { monthlyEquivalent: 31.25, yearlyAmount: 375 } },
        features: [
            { key: 'dashboards', label: 'Unlimited Dashboards', included: true },
            { key: 'lists', label: 'Unlimited Lists', included: true },
        ],
        capabilities: {
            biAnalytics: true,
            dataEnrichment: true,
            emailAndCalendar: true,
        },
    },
};
